import { checkDeleted, nameDescriptor } from 'pages/AutomationEngine/helpers';
import { type SearchRecordsStep } from '../actionSectionTypes';
import type {
  AddErrorMessage,
  MessageDictionary,
} from 'pages/AutomationEngine/messageDictionary';
import type {
  CustomObjectMinimal,
  FilterGroup,
} from '@kizen/api/custom-object';
import type {
  ConflictResolution,
  FilterType,
  SearchRecordsDetails,
} from './types';
import type { VariableNameAndType } from 'pages/AutomationEngine/dialogs/VariableWizard/types';

const forApi = (step: SearchRecordsStep) => {
  const details = { ...step.details };
  const {
    customObject,
    destinationVariable,
    destinationVariableResolution,
    filterType,
    filterGroups,
    filterConfig,
  } = details;

  const api = {
    details: {
      customObject: customObject?.value ? { id: customObject.value } : null,
      destinationVariable: destinationVariable?.value
        ? { name: destinationVariable.value }
        : null,
      destinationVariableResolution: destinationVariableResolution || null,
      filterType,
      filterGroups: filterGroups || [],
      filterConfig: filterConfig || null,
    },
  };

  return api;
};

type ApiObject = {
  customObject:
    | (SnakeToCamelCase<CustomObjectMinimal> & { deleted: boolean })
    | null;
  destinationVariable: (VariableNameAndType & { deleted: boolean }) | null;
  destinationVariableResolution: ConflictResolution | null;
  filterType: FilterType;
  filterGroups: FilterGroup[] | null;
  filterConfig: Record<string, any> | null;
};

const forFlow = (
  apiObject: ApiObject,
  addErrorMessage: AddErrorMessage,
  messageDictionary: MessageDictionary
): SearchRecordsDetails => {
  const {
    customObject,
    destinationVariable,
    destinationVariableResolution,
    filterType,
    filterGroups,
    filterConfig,
  } = apiObject;

  checkDeleted(
    messageDictionary,
    addErrorMessage,
    customObject,
    nameDescriptor,
    undefined,
    'customObject'
  );

  checkDeleted(
    messageDictionary,
    addErrorMessage,
    destinationVariable,
    nameDescriptor,
    undefined,
    'destinationVariable'
  );

  const invalidGroups = filterGroups?.some(
    ({ deleted, invalid }) => deleted || invalid
  );

  if (invalidGroups) {
    addErrorMessage(messageDictionary.thereWasanErrorLoadingTheAssociatedGroup);
  }

  if (filterConfig?.invalid) {
    addErrorMessage(
      messageDictionary.thereWasanErrorLoadingTheAssociatedFilters
    );
  }

  return {
    customObject: customObject && {
      value: customObject.id,
      label: customObject.deleted
        ? `${customObject.name} ${messageDictionary.deleted}`
        : customObject.objectName,
      deleted: customObject.deleted,
      data: customObject,
    },
    destinationVariable: destinationVariable && {
      value: destinationVariable.name,
      label: destinationVariable.deleted
        ? `${destinationVariable.name} ${messageDictionary.deleted}`
        : destinationVariable.name,
      deleted: destinationVariable.deleted,
      meta: destinationVariable,
    },
    destinationVariableResolution: destinationVariableResolution || null,
    filterType,
    filterGroups: filterGroups || [],
    filterConfig: filterConfig || null,
    hasError:
      customObject?.deleted ||
      destinationVariable?.deleted ||
      invalidGroups ||
      filterConfig?.invalid ||
      false,
  };
};

export const searchRecordsDTO = {
  forApi,
  forFlow,
};
